class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems =
        this.closest("cart-items") || this.closest("cart-drawer-items");
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define("cart-remove-button", CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));

    this.excludedProductIds = new Set();
    this.updateExcludedProductIds();
  }
  
  updateExcludedProductIds() {
    const excludedItems = document.querySelectorAll('[data-product-excluded-from-gwp="true"]');
    this.excludedProductIds.clear();
    excludedItems.forEach((item) => {
      const productId = item.getAttribute('data-product-id');
      if (productId) {
        this.excludedProductIds.add(parseInt(productId, 10));
      }
    });
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") {
          return;
        }

        this.onCartUpdate(event.cartData);
      }
    );

    if (
      window.themeGwpConfig &&
      window.themeGwpConfig.enabled &&
      !window.__gwpInitialSyncRequested
    ) {
      window.__gwpInitialSyncRequested = true;
      fetch(`${routes.cart_url}.js`)
        .then((response) => response.json())
        .then((cartState) =>
          this.ensureGiftWithPurchase(cartState, { publishOnChange: true })
        )
        .catch(() => {});
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Drawer-quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = "";

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace(
        "[min]",
        event.target.dataset.min
      );
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace(
        "[max]",
        event.target.max
      );
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace(
        "[step]",
        event.target.step
      );
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute("name"),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate(cartData) {
    if (this.tagName === "CART-DRAWER-ITEMS") {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          const selectors = [
            "cart-drawer-items",
            ".drawer__footer",
            ".drawer__header",
          ];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);

            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }

          this.updateExcludedProductIds();

          const cartCountElement = document.querySelectorAll(
            ".cart-count-bubble span"
          );

          const cartCountNumberElement = document
            .querySelector(".drawer__inner .drawer__header .drawer__heading")
            ?.textContent?.match(/Your Cart\((\d+)\)/);

          if (cartCountNumberElement) {
            const itemCount = cartCountNumberElement[1];
            cartCountElement.forEach((element) => {
              element.textContent = itemCount;
            });
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          const sourceQty = html.querySelector("cart-items");
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-contents",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer").dataset.id,
        selector: ".js-contents",
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then(async (response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        return this.ensureGiftWithPurchase(parsedState);
      })
      .then((parsedState) => {
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll(".cart-item");

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute("value");
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle("is-empty", parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector("cart-drawer");
        const cartFooter = document.getElementById("main-cart-footer");
        const bubbleElement = document.querySelector(
          ".cart-count-bubble > span:not(.visually-hidden)"
        );

        if (bubbleElement) {
          bubbleElement.textContent = parsedState.item_count;
        }

        if (cartFooter)
          cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
        if (cartDrawerWrapper)
          cartDrawerWrapper.classList.toggle(
            "is-empty",
            parsedState.item_count === 0
          );

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document
              .getElementById(section.id)
              .querySelector(section.selector) ||
            document.getElementById(section.id);

          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        this.updateExcludedProductIds();
        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
        let message = "";
        if (
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value, 10)
        ) {
          if (typeof updatedValue === "undefined") {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace(
              "[quantity]",
              updatedValue
            );
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(
                cartDrawerWrapper,
                lineItem.querySelector(`[name="${name}"]`)
              )
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper.querySelector(".drawer__inner-empty"),
            cartDrawerWrapper.querySelector("a")
          );
        } else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper,
            document.querySelector(".cart-item__name")
          );
        }
        const cartCountElement = document.querySelectorAll(
          ".cart-count-bubble span"
        );
        const cartCountNumberElement = document
          .querySelector(".drawer__inner .drawer__header .drawer__heading")
          ?.textContent?.match(/Your Cart\((\d+)\)/);

        if (cartCountNumberElement) {
          const itemCount = cartCountNumberElement[1];
          cartCountElement.forEach((element) => {
            element.textContent = itemCount;
          });
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-items",
          cartData: parsedState,
          variantId: variantId,
        });
      })
      .catch((e) => {
        this.querySelectorAll(".loading__spinner").forEach((overlay) =>
          overlay.classList.add("hidden")
        );
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError)
      lineItemError.querySelector(".cart-item__error-text").textContent =
        message;

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.add("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading__spinner`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove("hidden")
    );

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.remove("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading__spinner`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`
    );

    cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
    cartDrawerItemElements.forEach((overlay) =>
      overlay.classList.add("hidden")
    );
  }

  ensureGiftWithPurchase(cartState, options = {}) {
    const config = window.themeGwpConfig;
    if (!config || !config.enabled) {
      this.updateGwpBanner(cartState);
      return Promise.resolve(cartState);
    }

    const variantIds = config.variantIds && config.variantIds.length > 0
      ? config.variantIds.map(id => parseInt(id, 10)).filter(id => id > 0)
      : (config.variantId ? [parseInt(config.variantId, 10)].filter(id => id > 0) : []);

    if (variantIds.length === 0) {
      this.updateGwpBanner(cartState);
      return Promise.resolve(cartState);
    }

    const subtotalExGift = this.getSubtotalExcludingGift(cartState, variantIds);
    cartState.__gwpSubtotal = subtotalExGift;

    const desiredQty = this.calculateGiftQuantity(
      cartState,
      config,
      subtotalExGift
    );
    
    this.updateGwpBanner(cartState, desiredQty, subtotalExGift);

    let sameProduct = false;
    if (variantIds.length === 2 && config.productIds && config.productIds.length >= 2) {
      sameProduct = config.productIds[0] === config.productIds[1];
    } else if (variantIds.length === 2) {
      const item1 = (cartState.items || []).find(
        (item) => item.variant_id === variantIds[0]
      );
      const item2 = (cartState.items || []).find(
        (item) => item.variant_id === variantIds[1]
      );
      
      if (item1 && item2 && item1.product_id && item2.product_id) {
        sameProduct = item1.product_id === item2.product_id;
      }
    }
    
    let needsUpdate = false;
    const updates = {};
    
    if (sameProduct && variantIds.length === 2) {
      const items1 = (cartState.items || []).filter(
        (item) => item.variant_id === variantIds[0]
      );
      const items2 = (cartState.items || []).filter(
        (item) => item.variant_id === variantIds[1]
      );
      
      const currentQty1 = items1.reduce((sum, item) => sum + (item.quantity || 0), 0);
      const currentQty2 = items2.reduce((sum, item) => sum + (item.quantity || 0), 0);
      const currentTotal = currentQty1 + currentQty2;
      
      const targetQtyPerVariant = desiredQty;
      const targetTotal = targetQtyPerVariant * 2;
      
      const quantitiesMatch = currentQty1 === targetQtyPerVariant && currentQty2 === targetQtyPerVariant;
      const totalIsEven = currentTotal % 2 === 0;
      const hasDuplicates = items1.length > 1 || items2.length > 1;
      
      if (!quantitiesMatch || !totalIsEven || currentQty1 !== currentQty2 || hasDuplicates) {
        needsUpdate = true;
        
        if (hasDuplicates) {
          items1.forEach((item, index) => {
            if (index > 0 && item.variant_id === variantIds[0]) {
              updates[item.key] = 0;
            }
          });
          items2.forEach((item, index) => {
            if (index > 0 && item.variant_id === variantIds[1]) {
              updates[item.key] = 0;
            }
          });
        }
        
        updates[variantIds[0]] = targetQtyPerVariant;
        updates[variantIds[1]] = targetQtyPerVariant;
        
      }
    } else {
      variantIds.forEach(variantId => {
        const gwpItems = (cartState.items || []).filter(
          (item) => item.variant_id === variantId
        );
        
        const currentQty = gwpItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
        const hasDuplicates = gwpItems.length > 1;
        
        if (currentQty !== desiredQty || hasDuplicates) {
          needsUpdate = true;

          if (hasDuplicates) {
            gwpItems.forEach((item, index) => {
              if (index > 0 && item.variant_id === variantId) {
                updates[item.key] = 0;
              }
            });
          }
          
          updates[variantId] = desiredQty;
        }
      });
    }

    if (!needsUpdate) {
      return Promise.resolve(cartState);
    }

    const body = JSON.stringify({
      updates,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    return fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body })
      .then((response) => response.text())
      .then((state) => JSON.parse(state))
      .then((newState) => {
        const refreshedSubtotal = this.getSubtotalExcludingGift(
          newState,
          variantIds
        );
        newState.__gwpSubtotal = refreshedSubtotal;
        
        const refreshedDesiredQty = this.calculateGiftQuantity(
          newState,
          config,
          refreshedSubtotal
        );
        
        if (sameProduct && variantIds.length === 2) {
          const items1 = (newState.items || []).filter(
            (item) => item.variant_id === variantIds[0]
          );
          const items2 = (newState.items || []).filter(
            (item) => item.variant_id === variantIds[1]
          );
          
          const qty1 = items1.reduce((sum, item) => sum + (item.quantity || 0), 0);
          const qty2 = items2.reduce((sum, item) => sum + (item.quantity || 0), 0);
          const total = qty1 + qty2;
          const hasDuplicates = items1.length > 1 || items2.length > 1;
          
          if (qty1 !== qty2 || total % 2 !== 0 || qty1 !== refreshedDesiredQty || qty2 !== refreshedDesiredQty || hasDuplicates) {
            if (!options._recursionLimit) {
              const fixUpdates = {};
              
              if (hasDuplicates) {
                items1.forEach((item, index) => {
                  if (index > 0 && item.variant_id === variantIds[0]) {
                    fixUpdates[item.key] = 0;
                  }
                });
                items2.forEach((item, index) => {
                  if (index > 0 && item.variant_id === variantIds[1]) {
                    fixUpdates[item.key] = 0;
                  }
                });
              }
              
              fixUpdates[variantIds[0]] = refreshedDesiredQty;
              fixUpdates[variantIds[1]] = refreshedDesiredQty;
              
              const fixBody = JSON.stringify({
                updates: fixUpdates,
                sections: this.getSectionsToRender().map((section) => section.section),
                sections_url: window.location.pathname,
              });
              
              return fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body: fixBody })
                .then((response) => response.text())
                .then((state) => JSON.parse(state))
                .then((fixedState) => {
                  const finalSubtotal = this.getSubtotalExcludingGift(fixedState, variantIds);
                  fixedState.__gwpSubtotal = finalSubtotal;
                  this.updateGwpBanner(fixedState, refreshedDesiredQty, finalSubtotal);
                  if (options.publishOnChange) {
                    publish(PUB_SUB_EVENTS.cartUpdate, {
                      source: "gwp",
                      cartData: fixedState,
                    });
                  }
                  return fixedState;
                });
            }
          }
        }
        
        this.updateGwpBanner(newState, refreshedDesiredQty, refreshedSubtotal);
        if (options.publishOnChange) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "gwp",
            cartData: newState,
          });
        }
        return newState;
      })
      .catch((error) => {
        console.error("Unable to sync gift with purchase", error);
        return cartState;
      });
  }

  calculateGiftQuantity(
    cartState,
    config = window.themeGwpConfig || {},
    subtotalOverride
  ) {
    if (config.incrementAmount && config.incrementAmount > 0) {
      const subtotal = subtotalOverride != null
        ? subtotalOverride
        : (cartState.__gwpSubtotal != null ? cartState.__gwpSubtotal : 0);
      
      const increments = Math.floor(subtotal / config.incrementAmount);
      return increments;
    }

    const thresholds = Array.isArray(config.thresholds)
      ? config.thresholds
      : [];
    if (!thresholds.length) {
      return 0;
    }
    
    const variantIds = config.variantIds && config.variantIds.length > 0
      ? config.variantIds.map(id => parseInt(id, 10)).filter(id => id > 0)
      : (config.variantId ? [parseInt(config.variantId, 10)].filter(id => id > 0) : []);
    
    const subtotal = subtotalOverride != null
      ? subtotalOverride
      : (cartState.__gwpSubtotal != null
        ? cartState.__gwpSubtotal
        : this.getSubtotalExcludingGift(cartState, variantIds));
    
    let desiredQty = 0;
    thresholds.forEach((threshold) => {
      if (!threshold) {
        return;
      }
      const amount = Number(threshold.amount) || 0;
      const qty = Number(threshold.qty) || 0;
      if (amount > 0 && qty > 0 && subtotal >= amount) {
        desiredQty = Math.max(desiredQty, qty);
      }
    });
    return desiredQty;
  }

  updateGwpBanner(cartState, desiredQty = 0, subtotalOverride) {
    const banners = document.querySelectorAll("[data-gwp-banner]");
    if (!banners.length) {
      return;
    }
    const config = window.themeGwpConfig || {};
    
    if (config.incrementAmount && config.incrementAmount > 0) {
      const subtotal =
        subtotalOverride != null
          ? subtotalOverride
          : this.getSubtotalExcludingGift(cartState);
      
      const incrementsReached = Math.floor(subtotal / config.incrementAmount);
      const nextIncrementAmount = (incrementsReached + 1) * config.incrementAmount;
      const amountToNext = nextIncrementAmount - subtotal;
      
      let message = config.message || "";
      
      if (incrementsReached > 0) {
        message = "You've unlocked your free gift!";
      } else if (amountToNext > 0) {
        if (message && message.includes("[value]")) {
          message = message.replace(
            "[value]",
            this.formatMoney(config.incrementAmount)
          );
        }
        if (message && message.includes("[current]")) {
          message = message.replace("[current]", "first");
        }
        if (message && message.includes("[amount]")) {
          message = message.replace(
            "[amount]",
            this.formatMoney(amountToNext)
          );
        }
        if (!message || (!message.includes("[value]") && !message.includes("[amount]"))) {
          message = `Spend ${this.formatMoney(amountToNext)} more to unlock your free gift.`;
        }
      }
      
      banners.forEach((banner) => {
        const textTarget =
          banner.querySelector("[data-gwp-message]") || banner;
        if (message) {
          banner.classList.remove("gwp-banner--hidden");
          textTarget.textContent = message;
        } else {
          banner.classList.add("gwp-banner--hidden");
        }
      });
      return;
    }
    
    const thresholds = Array.isArray(config.thresholds)
      ? config.thresholds
      : [];
    const subtotal =
      subtotalOverride != null
        ? subtotalOverride
        : this.getSubtotalExcludingGift(cartState);
    const sorted = thresholds
      .filter((threshold) => threshold && Number(threshold.amount) > 0)
      .sort((a, b) => Number(a.amount) - Number(b.amount));

    let nextThreshold = null;
    let thresholdIndex = -1;
    for (let i = 0; i < sorted.length; i += 1) {
      if (subtotal < Number(sorted[i].amount)) {
        nextThreshold = sorted[i];
        thresholdIndex = i;
        break;
      }
    }

    let message = config.message || "";
    if (nextThreshold && thresholdIndex >= 0) {
      const thresholdAmount = Number(nextThreshold.amount);
      const difference = thresholdAmount - subtotal;
      if (difference > 0) {
        const ordinalWord = this.getOrdinalWord(thresholdIndex);
        
        if (message && message.includes("[current]")) {
          message = message.replace("[current]", ordinalWord);
        }
        
        if (message && message.includes("[value]")) {
          message = message.replace(
            "[value]",
            this.formatMoney(thresholdAmount)
          );
        } else if (message && message.includes("[amount]")) {
          message = message.replace(
            "[amount]",
            this.formatMoney(difference)
          );
        } else if (message) {
          message = `${message} (${this.formatMoney(difference)} to go)`;
        } else {
          message = `Spend ${this.formatMoney(
            difference
          )} more to unlock your free gift.`;
        }
      }
    } else if (desiredQty && desiredQty > 0) {
      message = "You've unlocked your free gift!";
    }

    banners.forEach((banner) => {
      const textTarget =
        banner.querySelector("[data-gwp-message]") || banner;
      if (message) {
        banner.classList.remove("gwp-banner--hidden");
        textTarget.textContent = message;
      } else {
        banner.classList.add("gwp-banner--hidden");
      }
    });
  }

  getOrdinalWord(index) {
    const ordinals = ["first", "second", "third"];
    if (index >= 0 && index < ordinals.length) {
      return ordinals[index];
    }
    return `#${index + 1}`;
  }

  formatMoney(value) {
    if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
      const format =
        (window.theme && window.theme.moneyFormat) ||
        Shopify.money_format ||
        "${{amount}}";
      return Shopify.formatMoney(value, format);
    }

    try {
      const formatter = new Intl.NumberFormat(undefined, {
        style: "currency",
        currency:
          (window.Shopify &&
            Shopify.currency &&
            Shopify.currency.active) ||
          "USD",
      });
      return formatter.format(value / 100);
    } catch (error) {
      return (value / 100).toFixed(2);
    }
  }

  getSubtotalExcludingGift(cartState, variantIds) {
    const config = window.themeGwpConfig || {};
    
    let giftVariantIds = [];
    if (Array.isArray(variantIds)) {
      giftVariantIds = variantIds.map(id => parseInt(id, 10)).filter(id => id > 0);
    } else if (variantIds) {
      const singleId = parseInt(variantIds, 10);
      if (singleId > 0) {
        giftVariantIds = [singleId];
      }
    }
    
    if (giftVariantIds.length === 0) {
      if (config.variantIds && config.variantIds.length > 0) {
        giftVariantIds = config.variantIds.map(id => parseInt(id, 10)).filter(id => id > 0);
      } else if (config.variantId) {
        const singleId = parseInt(config.variantId, 10);
        if (singleId > 0) {
          giftVariantIds = [singleId];
        }
      }
    }
    
    const exclusionTag = (config.exclusionTag || '').trim();
    
    if (
      !cartState ||
      !Array.isArray(cartState.items) ||
      !cartState.items.length
    ) {
      return cartState && cartState.items_subtotal_price
        ? cartState.items_subtotal_price
        : 0;
    }

    let subtotal = 0;
    cartState.items.forEach((item) => {
      if (!item) {
        return;
      }

      if (giftVariantIds.length > 0 && giftVariantIds.includes(item.variant_id)) {
        return;
      }

      if (exclusionTag) {
        if (item.product && Array.isArray(item.product.tags)) {
          if (item.product.tags.includes(exclusionTag)) {
            return;
          }
        }
        else if (item.product_id) {
          const productId = parseInt(item.product_id, 10);
          if (this.excludedProductIds.has(productId)) {
            return;
          }
        }
      }
      
      const linePrice =
        typeof item.final_line_price === "number"
          ? item.final_line_price
          : item.original_line_price || 0;
      subtotal += linePrice;
    });
    return subtotal;
  }
}

customElements.define("cart-items", CartItems);

if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          "input",
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
